import { useEffect, useState } from "react";
import { Package2, Plus, Save, X, Trash2, Sparkles, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { readError } from "../api";
import { money, emptyItem, uploadFile } from "../studioUtils";

const emptyTemplate = () => ({
  name: "", description: "",
  items: [emptyItem()],
  default_notes: "Fiyatlar nakit fiyatıdır.",
  default_payment_terms: "",
  default_delivery_terms: "",
});

export default function QuoteTemplates({ onApply }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [activeRow, setActiveRow] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/quote-templates"); setList(data); }
    catch (err) { toast.error(readError(err)); }
  };
  useEffect(() => {
    load();
    api.get("/products/all").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  const openNew = () => setEditing(emptyTemplate());
  const openEdit = (tpl) => setEditing({ ...tpl, items: tpl.items.length ? tpl.items : [emptyItem()] });

  const save = async () => {
    if (!editing.name.trim()) return toast.error("Şablon adı zorunlu");
    const items = (editing.items || []).filter(it => it.product_name.trim());
    if (!items.length) return toast.error("En az bir ürün ekleyin");
    setBusy(true);
    try {
      if (editing.id) {
        await api.put(`/quote-templates/${editing.id}`, { ...editing, items });
        toast.success("Şablon güncellendi");
      } else {
        await api.post("/quote-templates", { ...editing, items });
        toast.success("Şablon kaydedildi");
      }
      setEditing(null); load();
      api.get("/products/all").then(({ data }) => setCatalog(data)).catch(() => {});
    } catch (err) { toast.error(readError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`"${name}" şablonu silinsin mi?`)) return;
    try { await api.delete(`/quote-templates/${id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(readError(err)); }
  };

  const applyToQuote = (tpl) => { onApply && onApply(tpl); };

  const updateItem = (i, k, v) => setEditing(t => ({
    ...t, items: t.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it),
  }));
  const matchingProducts = (name) => {
    const query = name.trim().toLocaleLowerCase("tr-TR");
    return query.length >= 2 ? catalog.filter(p => [p.name, p.sku].some(v =>
      (v || "").toLocaleLowerCase("tr-TR").includes(query))).slice(0, 8) : [];
  };
  const chooseProduct = (index, product) => {
    setEditing(t => ({ ...t, items: t.items.map((row, i) => i === index ? {
      ...row, product_name: product.name, sku: product.sku || "", image_url: product.image_url || "",
      list_price: product.price_currency === "USD" ? Number(product.default_price || 0) : 0,
    } : row) }));
    setActiveRow(null);
    if (product.price_currency !== "USD") toast.info(`${product.name}: katalog fiyatı ${product.price_currency}. USD fiyatını elle girin.`);
  };
  const uploadItemImage = async (index, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > 5 * 1024 * 1024) return toast.error("5 MB altında bir görsel seçin");
    try { const result = await uploadFile(file); updateItem(index, "image_url", result.absoluteUrl); }
    catch (err) { toast.error(readError(err, "Görsel yüklenemedi")); }
  };

  return (
    <section className="templates-view" data-testid="templates-view">
      <div className="catalog-intro">
        <div className="settings-icon"><Package2 size={22} /></div>
        <div>
          <p className="eyebrow blue">HAZIR PAKETLER</p>
          <h2>Teklif Şablonları</h2>
          <p>Sık kullandığınız ürün kombinasyonlarını kaydedin, yeni tekliflerde tek tıkla dolduran.</p>
        </div>
      </div>

      <div className="catalog-actions">
        <button data-testid="new-template-button" className="primary-button" onClick={openNew}>
          <Plus size={16} /> Yeni şablon
        </button>
      </div>

      {list.length === 0 ? (
        <div className="empty-state">
          <Package2 size={30} />
          <h2>Henüz şablon yok</h2>
          <p>"Yeni şablon" ile başlayın.</p>
        </div>
      ) : (
        <div className="template-grid">
          {list.map(tpl => (
            <div className="template-card" key={tpl.id} data-testid={`template-card-${tpl.name}`}>
              <div className="tpl-head">
                <div>
                  <b>{tpl.name}</b>
                  <p>{tpl.description || `${tpl.items.length} ürün`}</p>
                </div>
                <span className="tpl-count">{tpl.items.length} ürün</span>
              </div>
              <ul className="tpl-items">
                {tpl.items.slice(0, 4).map((it, i) => (
                  <li key={i}>
                    <span>{it.product_name}</span>
                    <em>{it.quantity} × {money(it.list_price, "$")}</em>
                  </li>
                ))}
                {tpl.items.length > 4 && <li className="more">+ {tpl.items.length - 4} daha</li>}
              </ul>
              <div className="tpl-actions">
                {onApply && (
                  <button className="primary-button" onClick={() => applyToQuote(tpl)}
                    data-testid={`apply-template-${tpl.name}`}>
                    <Sparkles size={14} /> Teklife uygula
                  </button>
                )}
                <button className="outline-button" onClick={() => openEdit(tpl)}
                  data-testid={`edit-template-${tpl.name}`}>
                  Düzenle
                </button>
                <button className="icon-button" onClick={() => remove(tpl.id, tpl.name)}
                  data-testid={`delete-template-${tpl.name}`} title="Sil">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal-card wide-modal" onClick={e => e.stopPropagation()} data-testid="template-modal">
            <button className="modal-close" onClick={() => setEditing(null)}><X size={16} /></button>
            <h2>{editing.id ? "Şablonu düzenle" : "Yeni şablon"}</h2>
            <div className="settings-grid">
              <label>Şablon adı<input data-testid="tpl-name" value={editing.name}
                onChange={e => setEditing({ ...editing, name: e.target.value })} placeholder="Ör: Standart Güvenlik Paketi" /></label>
              <label>Açıklama<input data-testid="tpl-desc" value={editing.description}
                onChange={e => setEditing({ ...editing, description: e.target.value })} placeholder="İsteğe bağlı" /></label>
              <label>Teslimat<input value={editing.default_delivery_terms}
                onChange={e => setEditing({ ...editing, default_delivery_terms: e.target.value })} placeholder="Ör: 5 iş günü" /></label>
              <label>Ödeme<input value={editing.default_payment_terms}
                onChange={e => setEditing({ ...editing, default_payment_terms: e.target.value })} placeholder="Ör: Peşin" /></label>
              <label className="full-field">Notlar
                <textarea rows={2} value={editing.default_notes}
                  onChange={e => setEditing({ ...editing, default_notes: e.target.value })} />
              </label>
            </div>

            <h3 style={{ marginTop: 18, marginBottom: 8, font: "600 13px 'Plus Jakarta Sans'" }}>Ürünler</h3>
            <p className="muted">Ürün adını yazdıkça kayıtlı ürünler önerilir. Yeni ürünler şablon kaydedildiğinde kataloğa eklenir. Şablon fiyatları USD cinsindedir.</p>
            <div className="tpl-items-table">
              <div className="tpl-item-row tpl-head">
                <span>Görsel</span><span>Ürün adı</span><span>SKU</span><span>Adet</span><span>Liste $</span><span>İsk.%</span><span></span>
              </div>
              {editing.items.map((it, i) => (
                <div className="tpl-item-row" key={i}>
                  <label className="tpl-image"><input type="file" accept="image/*" hidden onChange={e => uploadItemImage(i, e.target.files?.[0])} />
                    {it.image_url ? <img src={it.image_url} alt="Ürün" /> : <ImageIcon size={18} />}</label>
                  <div className="tpl-product-field"><input value={it.product_name} placeholder="Ürün adı yazın..."
                    onFocus={() => setActiveRow(i)} onBlur={() => setTimeout(() => setActiveRow(null), 160)}
                    onChange={e => { updateItem(i, "product_name", e.target.value); setActiveRow(i); }}
                    data-testid={`tpl-item-name-${i}`} />
                    {activeRow === i && matchingProducts(it.product_name).length > 0 &&
                      <div className="suggestions tpl-suggestions">{matchingProducts(it.product_name).map(p =>
                        <button type="button" key={p.name} onMouseDown={e => e.preventDefault()} onClick={() => chooseProduct(i, p)}>
                          {p.image_url && <img src={p.image_url} alt="" className="suggestion-thumb" />}
                          <div className="sugg-text"><b>{p.name}</b><em>{p.sku || "SKU yok"}</em></div>
                        </button>)}</div>}
                  </div>
                  <input value={it.sku || ""} placeholder="SKU"
                    onChange={e => updateItem(i, "sku", e.target.value)} />
                  <input type="number" min="0" value={it.quantity} onFocus={e => e.target.select()}
                    onChange={e => updateItem(i, "quantity", Number(e.target.value) || 0)} />
                  <input type="number" min="0" step="0.01" value={it.list_price} onFocus={e => e.target.select()}
                    onChange={e => updateItem(i, "list_price", Number(e.target.value) || 0)} />
                  <input type="number" min="0" max="100" value={it.discount} onFocus={e => e.target.select()}
                    onChange={e => updateItem(i, "discount", Number(e.target.value) || 0)} />
                  <button className="icon-button" onClick={() => setEditing(t => ({ ...t,
                    items: t.items.filter((_, idx) => idx !== i) }))}><Trash2 size={13} /></button>
                </div>
              ))}
              <button className="add-line" data-testid="tpl-add-item"
                onClick={() => setEditing(t => ({ ...t, items: [...t.items, emptyItem()] }))}>
                <Plus size={13} /> Ürün ekle
              </button>
            </div>

            <div className="settings-actions">
              <button className="outline-button" onClick={() => setEditing(null)}>Vazgeç</button>
              <button className="primary-button" onClick={save} disabled={busy} data-testid="tpl-save">
                <Save size={16} /> {busy ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
